import { useCallback, useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"

import { Elements } from "@stripe/react-stripe-js"
import { loadStripe } from "@stripe/stripe-js"

import styles from "./Payment.module.scss"
import StripeForm, { PaymentMethodType } from "./StripeForm"
import { isEmailRequired, isValidEmailForPayment } from "./logic"

import invoiceImage from "@/lib/images/invoice.svg"
import apiService from "@/provider/rest"
import CancelFeeLotteryBanner from "@/public/components/features/payment/CancelFeeLottery/CancelFeeLotteryBanner"
import CancelFeeLotteryTermsSection from "@/public/components/features/payment/CancelFeeLottery/CancelFeeLotteryTermsSection"
import {
	CancelFeeLottery,
	toCancelFeeLottery,
} from "@/public/components/features/payment/CancelFeeLottery/cancelFeeLottery"
import { EmailFormLabel } from "@/public/components/features/payment/EmailFormLabel"
import { usePaymentEvent } from "@/public/event"

/**
 * - bank transfer: 一気に全額振込でない場合、振込反映され次第、残り残額だけ支払いする形になる。partial paid の場合は、コンビニ決済以外可能
 * - card
 * - apple pay
 * - google pay
 *
 * - konbini: partial paid 状態でなければ利用可能。コンビニ決済のときだけ、デフォルトの stripe PaymentIntent ではなく、コンビニ決済専用の
 *   PaymentIntent を利用する。理由はコンビニ決済を選択肢し、支払いボタンを押した時 (confirm した時) 他の支払い手段で confirm ができなくなるので、
 *   intent をわkせている
 *
 * TODO: stripe支払いは終わったが、invoice_paidが失敗している場合にどう見せるか検討する
 */
function Payment() {
	const navigate = useNavigate()
	const stripePromise = process.env.REACT_APP_STRIPE_KEY
		? loadStripe(process.env.REACT_APP_STRIPE_KEY)
		: null

	const paymentID = useParams().payment_id
	const location = useLocation()
	const {
		openPaymentPage,
		showCancelFeeLotteryBanner,
		openCancelFeeLotteryRoulette,
		closeCancelFeeLotteryRouletteWithoutSpinning,
	} = usePaymentEvent()

	/* states */
	const [stripeClientSecret, setStripeClientSecret] = useState<
		string | undefined
	>(undefined)
	const [konbiniClientSecret, setKonbiniClientSecret] = useState<
		string | undefined
	>(undefined)
	const [konbiniHostedVoucherUrl, setKonbiniHostedVoucherUrl] = useState<
		string | null
	>(null)
	const [amountRemaining, setAmountRemaining] = useState<number | undefined>(
		undefined
	)
	const [isPartialPayment, setIsPartialPayment] = useState(false)
	const [showCouponDetail, setShowCouponDetail] = useState(false)
	// salon icon の読み込みに失敗した時は icon を出さず salon 名だけを強調表示する
	// (再予約画面 RereservationPageV2 と同じ扱い)
	const [isIconLoadError, setIsIconLoadError] = useState(false)
	// header 画像が 404 等で読み込めない時は banner 枠ごと消す。
	// 枠を残すと broken image アイコンと alt テキストが出て見た目が崩れるため。
	const [isHeaderImageLoadError, setIsHeaderImageLoadError] = useState(false)
	const [email, setEmail] = useState("")
	const [cancelData, setCancelData] = useState({
		id: "",
		name: "",
		phoneNumber: "",
		amount: 0,
		expDate: "",
		payment: {
			salonName: "",
			salonAddress: "",
			salonIconUrl: "",
			salonHeaderImageUrl: "",
			showsSalonHeader: false,
			paymentDescriptions: [""],
		},
		is_aborted: false,
		couponMaster: {
			id: "",
			name: "",
			targetName: "",
			discountAmount: 0,
			discountPercentage: 0,
			couponDiscountAmount: 0,
			expire_duration_day: 0,
			min_amount: 0,
			precautions: [],
			showDiscountAmount: false,
			isRigakuBody: false,
		} as any,
	})
	const [paymentMethod, setPaymentMethod] = useState<PaymentMethodType>("card")
	// 個社対応の問い合わせリンク (BE が feature flag で出し分けて URL を返す)
	const [companyContactLinkUrl, setCompanyContactLinkUrl] = useState<
		string | null
	>(null)
	// キャンセル料お支払い 10 万円ルーレット. 対象の請求で、支払い期限内の時だけ BE が返す.
	const [cancelFeeLottery, setCancelFeeLottery] =
		useState<CancelFeeLottery | null>(null)
	const couponMasterExists =
		cancelData.couponMaster && cancelData.couponMaster.name
	const couponDiscountAmountText = useMemo(() => {
		if (couponMasterExists) {
			if (cancelData.couponMaster.discountAmount) {
				return "￥" + cancelData.couponMaster.couponDiscountAmount
			} else if (cancelData.couponMaster.discountPercentage) {
				return (
					"￥" +
					cancelData.couponMaster.couponDiscountAmount +
					" (お支払い金額の" +
					cancelData.couponMaster.discountPercentage +
					"%から二桁円以下を切り捨てた金額)"
				)
			}
		}
		return ""
	}, [cancelData.couponMaster, couponMasterExists])

	const options = useMemo(() => {
		return {
			clientSecret: stripeClientSecret,
			appearance: {
				theme: "stripe" as const, // "stripe" | "night" | "flat" のいずれかにする
			},
		}
	}, [stripeClientSecret])

	// コンビニ決済でconfirm済みの場合はメール入力欄を非表示にする
	const showKonbiniVoucherButton =
		paymentMethod === "konbini" && konbiniHostedVoucherUrl !== null

	// 部分支払い済みの場合はコンビニ決済を無効にする
	const isKonbiniDisabledDueToPartialPayment =
		paymentMethod === "konbini" && isPartialPayment

	const getCancelData = async () => {
		const res = await apiService.get("/public/payment/" + paymentID)
		if (res.payment_status === 200) {
			navigate("/payment/complete/" + paymentID)
		}
		setCompanyContactLinkUrl(res.company_contact_link_url ?? null)
		setCancelFeeLottery(toCancelFeeLottery(res.lottery))
		setCancelData({
			id: res.cancel_id,
			name: res.name,
			phoneNumber: res.phone_number ?? "",
			amount: res.invoice_price,
			expDate: res.payment_deadline,
			payment: {
				salonName: res.salon.name,
				salonAddress: res.salon.address,
				salonIconUrl: res.salon.icon_url ?? "",
				salonHeaderImageUrl: res.salon.header_image_url ?? "",
				// feature flag (SHOW_SALON_HEADER_ON_PAYMENT_PAGE) が有効な店舗だけ true
				showsSalonHeader: res.salon.shows_salon_header ?? false,
				paymentDescriptions: res.payment_descriptions,
			},
			is_aborted: res.is_aborted,
			couponMaster: res.coupon_master
				? {
						id: res.coupon_master.id,
						name: res.coupon_master.name,
						targetName: res.coupon_master.target_name,
						discountAmount: res.coupon_master.discount_amount,
						discountPercentage: res.coupon_master.discount_percent,
						couponDiscountAmount: res.coupon_master.coupon_discount_amount,
						expire_duration_day: res.coupon_master.expire_duration_day,
						min_amount: res.coupon_master.min_amount,
						precautions: res.coupon_master.precautions,
						showDiscountAmount: res.coupon_master.show_discount_amount,
						isRigakuBody: res.coupon_master.is_rigaku_body,
					}
				: null,
		})
	}
	// ルーレットを回し終えたら、くじ券を見せたことをサーバに記録する (lottery_tickets.shown_time).
	// 「くじ券の発行数」と「実際に見せた数」を分けて測るための記録で、当選口数・応募には関係しない.
	// 記録に失敗してもお客様の画面・お支払いは変わらないので、エラーは握って無視する.
	const markCancelFeeLotteryAsShown = useCallback(() => {
		apiService
			.post(`/public/payment/${paymentID}/lottery/shown`, {})
			.catch(() => undefined)
	}, [paymentID])

	// 見せ方 A/B の auto_open 群は、請求画面を開いた直後にルーレットを自動で開いて回し始める.
	// ただしコンビニ/銀行振込で一度 confirm したあと modal を閉じて戻ってきた時 (query にメアドが付く) は
	// 支払いの途中なので被せない.
	const isReturningFromPaymentConfirm =
		new URLSearchParams(location.search).get("email") !== null
	let autoOpensCancelFeeLotteryRoulette = false
	if (cancelFeeLottery !== null && !isReturningFromPaymentConfirm) {
		autoOpensCancelFeeLotteryRoulette = cancelFeeLottery.autoOpenRoulette
	}

	const getStripeClientSecret = async () => {
		const res = await apiService.get("/public/payment/intent/" + paymentID)
		setStripeClientSecret(res.client_secret)
		if (res.konbini_client_secret) {
			// konbini 決済導入後の Payment のみ、client secret が帰ってくる
			setKonbiniClientSecret(res.konbini_client_secret)
		}
		setKonbiniHostedVoucherUrl(res.konbini_hosted_voucher_url)
		setAmountRemaining(res.amount_remaining)
		setIsPartialPayment(res.is_partial_payment ?? true)
	}

	useEffect(() => {
		getCancelData()
		getStripeClientSecret()
	}, [paymentID])

	// fe-event: 請求書画面を開いた
	useEffect(() => {
		openPaymentPage()
	}, [openPaymentPage])

	// 銀行振込 orコンビニ支払いで一度 confirm したあと modal を閉じると、query param にメアドをいれてこの画面にくるので
	// その時ようの処理
	useEffect(() => {
		const queryParams = new URLSearchParams(location.search)
		setEmail(queryParams.get("email") ?? "")
	}, [location.search])

	const toggleCouponDetail = () => {
		setShowCouponDetail(!showCouponDetail)
	}

	const showsSalonIcon =
		cancelData.payment.salonIconUrl !== "" && !isIconLoadError
	const showsHeaderImage =
		cancelData.payment.salonHeaderImageUrl !== "" && !isHeaderImageLoadError

	return (
		<div>
			{cancelData.is_aborted ? (
				<div className={styles.payment_aborted}>
					請求は取り消しされました。
					<br />
					またのご来店をお待ちしております。
					<br />
					<br />
					{cancelData.payment.salonName}
					<br />
					{cancelData.payment.salonAddress}
				</div>
			) : (
				<div className={styles.payment_page}>
					{cancelData.payment.showsSalonHeader ? (
						<>
							{/* 再予約画面 (RereservationPageV2) と同じ構成の header:
							    上部の icon + タイトルバー -> salon header banner -> salon icon + salon 名 */}
							<div className={styles.header}>
								<img
									src={invoiceImage}
									alt="請求書"
									className={styles.headerIcon}
								/>
								<div className={styles.headerText}>請求書</div>
							</div>
							{/* 大きい header banner: salon の header 画像があり、かつ読み込めた時のみ表示する.
							    salon icon + 名前は header 画像の有無に関わらず常に下に表示する. */}
							{showsHeaderImage && (
								<div className={styles.headerBanner}>
									<img
										src={cancelData.payment.salonHeaderImageUrl}
										alt="salon_header"
										className={styles.headerBannerImage}
										onError={() => setIsHeaderImageLoadError(true)}
									/>
								</div>
							)}
							<div
								className={`${styles.salonHeader} ${showsSalonIcon ? "" : styles.salonHeaderNoImage}`}
							>
								{showsSalonIcon && (
									<img
										src={cancelData.payment.salonIconUrl}
										alt="salon_icon"
										className={styles.salonIcon}
										onError={() => setIsIconLoadError(true)}
									/>
								)}
								<div
									className={`${styles.salonTitle} ${showsSalonIcon ? "" : styles.salonTitleEmphasis}`}
								>
									{cancelData.payment.salonName}
								</div>
							</div>
						</>
					) : (
						<div className={styles.payment_header}>請求書</div>
					)}
					<div className={styles.payment_body}>
						{/* ルーレットは請求内容より先に見せる (支払いの動機づけとして最初に目に入る位置に置く) */}
						{cancelFeeLottery !== null && (
							<div className={styles.lottery}>
								<CancelFeeLotteryBanner
									lottery={cancelFeeLottery}
									cancelId={cancelData.id}
									customerName={cancelData.name}
									paymentDeadline={cancelData.expDate}
									autoOpen={autoOpensCancelFeeLotteryRoulette}
									onBannerShown={showCancelFeeLotteryBanner}
									onShown={markCancelFeeLotteryAsShown}
									onRouletteOpen={openCancelFeeLotteryRoulette}
									onRouletteClosedWithoutSpinning={
										closeCancelFeeLotteryRouletteWithoutSpinning
									}
								/>
							</div>
						)}
						<h3 className={styles.subtitle}>ご請求内容</h3>
						{cancelData.payment.paymentDescriptions.map((desc, i) => {
							return (
								<p key={i} className={styles.payment_desc}>
									{desc}
								</p>
							)
						})}
						<table className={styles.payment_detail_table}>
							<tbody>
								<tr>
									<td className={styles.td_left}>お名前</td>
									<td className={styles.td_right}>{cancelData.name} 様</td>
								</tr>
								<tr>
									<td className={styles.td_left}>ご請求金額</td>
									<td className={styles.td_right}>￥{amountRemaining ?? ""}</td>
								</tr>
								<tr>
									<td className={styles.td_left}>お支払い期日</td>
									<td className={styles.td_right}>{cancelData.expDate}</td>
								</tr>
								<tr>
									<td className={styles.td_left}>お支払い先</td>
									<td className={styles.td_right}>
										{cancelData.payment.salonName}
										<br />
										{cancelData.payment.salonAddress}
									</td>
								</tr>
								{couponMasterExists && (
									<tr>
										<td className={styles.td_left}>お支払い特典</td>
										<td className={styles.td_right}>
											{!cancelData.couponMaster.isRigakuBody && (
												<>
													期日までに支払うと
													<br />
												</>
											)}
											<b>{cancelData.couponMaster.name}</b>
											をプレゼント
										</td>
									</tr>
								)}
							</tbody>
						</table>
						{couponMasterExists && (
							<>
								<div
									className={styles.coupon_detail}
									style={{
										maxHeight: showCouponDetail ? "1000px" : "0px",
										overflow: "hidden",
										transition: "max-height 0.5s ease-out",
									}}
								>
									<h4 className={styles.section_title}>
										{cancelData.couponMaster.isRigakuBody
											? "施術券内容"
											: "クーポン内容"}
									</h4>
									<table
										className={`${styles.coupon} ${styles.payment_detail_table}`}
									>
										<tbody>
											<tr>
												<td className={styles.td_left}>割引対象</td>
												<td className={styles.td_right}>
													{cancelData.couponMaster.targetName}
												</td>
											</tr>
											{cancelData.couponMaster.showDiscountAmount && (
												<tr>
													<td className={styles.td_left}>割引金額</td>
													<td className={styles.td_right}>
														{couponDiscountAmountText}
													</td>
												</tr>
											)}
											<tr>
												<td className={styles.td_left}>ご利用方法</td>
												<td className={styles.td_right}>
													{cancelData.couponMaster.isRigakuBody
														? "次回来店時に施術券提示"
														: "次回来店時にクーポン提示"}
												</td>
											</tr>
											{!cancelData.couponMaster.isRigakuBody && (
												<tr>
													<td className={styles.td_left}>有効期限</td>
													<td className={styles.td_right}>
														お支払い後
														{cancelData.couponMaster.expire_duration_day}
														日以内
													</td>
												</tr>
											)}
											<tr>
												<td className={styles.td_left}>利用回数</td>
												<td className={styles.td_right}>
													{cancelData.couponMaster.isRigakuBody
														? "1回限り有効（無期限）"
														: "期限内で1回限り有効"}
												</td>
											</tr>
											<tr>
												<td className={styles.td_left}>最低利用金額</td>
												<td className={styles.td_right}>
													￥{cancelData.couponMaster.min_amount}
												</td>
											</tr>
										</tbody>
									</table>
									<h4 className={styles.section_title}>注意事項</h4>
									<ul className={styles.payment_ul}>
										{cancelData?.couponMaster?.precautions?.map(
											(precaution, _) => <li>{precaution}</li>
										)}
									</ul>
								</div>
								<p
									className={styles.coupon_detail_switch}
									onClick={toggleCouponDetail}
								>
									{showCouponDetail ? "詳細を閉じる" : "詳細を見る"}
								</p>
							</>
						)}

						{!showKonbiniVoucherButton &&
							!isKonbiniDisabledDueToPartialPayment && (
								<>
									<div className={styles.email_form}>
										<EmailFormLabel
											required={isEmailRequired(paymentMethod)}
											couponMasterExists={couponMasterExists}
										/>
										<input
											type="email"
											placeholder="example@gmail.com"
											value={email}
											onChange={(e) => {
												setEmail(e.target.value)
											}}
										/>
									</div>
									<div className={styles.warning}>
										{!isValidEmailForPayment(paymentMethod, email) && (
											<span>メールアドレスを正しく入力してください</span>
										)}
									</div>
								</>
							)}
						<h3 className={`${styles.payment_method} ${styles.subtitle}`}>
							お支払い方法
						</h3>
						<div>
							{paymentID && stripeClientSecret && amountRemaining && (
								// keyを指定することで、payment method切り替え時にElementsを再マウント
								<Elements options={options} stripe={stripePromise}>
									<StripeForm
										amount={amountRemaining}
										paymentID={paymentID}
										name={cancelData.name}
										phoneNumber={cancelData.phoneNumber}
										email={email}
										paymentMethod={paymentMethod}
										onChangePaymentMethod={setPaymentMethod}
										konbiniHostedVoucherUrl={konbiniHostedVoucherUrl}
										konbiniClientSecret={konbiniClientSecret}
										isPartialPayment={isPartialPayment}
										showKonbiniVoucherButton={showKonbiniVoucherButton}
									/>
								</Elements>
							)}
							<hr />
						</div>
						<div className={styles.notes}>
							<div>※留意事項</div>
							<div>入れ違いで請求書通知が届く場合がございます</div>
							<div>振込確認完了まで銀行の営業日で1日ほどかかります</div>
							<div>振込確認が完了後、決済完了通知が届きます</div>
							{companyContactLinkUrl && (
								<div className={styles.contact_link}>
									<a
										href={companyContactLinkUrl}
										target="_blank"
										rel="noopener noreferrer"
									>
										行き違い等お問い合わせはこちら
									</a>
								</div>
							)}
						</div>
					</div>
					{/* 抽選キャンペーンの規約. バナーの「詳細はキャンペーン規約（ページ下部）」の飛び先.
					    枠の左右は ご請求内容の灰色ボックス (payment_body) と揃える */}
					{cancelFeeLottery !== null && (
						<div className={styles.lottery_terms}>
							<CancelFeeLotteryTermsSection
								maxWheelMultiplier={Math.max(
									...cancelFeeLottery.wheelMultipliers
								)}
							/>
						</div>
					)}
					<div className={styles.footer}>請求書コード: {paymentID}</div>
				</div>
			)}
		</div>
	)
}

export default Payment
